/**
 * Which colour scheme the web app draws: the person's choice, and the scheme
 * it resolves to right now.
 *
 * Light until the person picks otherwise: a first visit draws light whatever
 * the computer is set to, and Dark or Follow system holds only once chosen in
 * Settings › Appearance. A choice already stored is kept as it is. `system`
 * follows the operating system and changes with it. The resolved scheme is
 * written to `<html class="dark">`, which is the one switch the generated
 * theme (src/styles/theme.css) reads. The choice survives a reload in
 * localStorage; a browser that refuses storage keeps the default. index.html
 * applies the same rule before the first paint.
 */
import { create } from 'zustand';

export type SchemeChoice = 'system' | 'light' | 'dark';
export type Scheme = 'light' | 'dark';

const STORAGE_KEY = 'alluzo.scheme';

/** What a browser that never chose draws. */
export const DEFAULT_SCHEME_CHOICE: SchemeChoice = 'light';

/** A stored value as a choice: anything unknown, or nothing, is the default. */
export const schemeChoiceOf = (stored: string | null): SchemeChoice =>
  stored === 'light' || stored === 'dark' || stored === 'system' ? stored : DEFAULT_SCHEME_CHOICE;

const media = (): MediaQueryList | null =>
  typeof window === 'undefined' ? null : window.matchMedia('(prefers-color-scheme: dark)');

const readChoice = (): SchemeChoice => {
  try {
    return schemeChoiceOf(localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_SCHEME_CHOICE;
  }
};

const resolve = (choice: SchemeChoice): Scheme =>
  choice === 'system' ? (media()?.matches === true ? 'dark' : 'light') : choice;

/**
 * Every colour flips at once. Controls ease their colour on hover, and without
 * this a scheme change would fade each one separately over 120ms.
 */
const apply = (scheme: Scheme): void => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (root.classList.contains('dark') === (scheme === 'dark')) return;
  root.classList.add('scheme-switching');
  root.classList.toggle('dark', scheme === 'dark');
  // Read a style so the switch paints before transitions come back.
  void window.getComputedStyle(root).color;
  requestAnimationFrame(() => root.classList.remove('scheme-switching'));
};

type ThemeState = {
  choice: SchemeChoice;
  scheme: Scheme;
  setChoice: (choice: SchemeChoice) => void;
};

export const useThemeStore = create<ThemeState>((set) => {
  const choice = readChoice();
  const scheme = resolve(choice);
  apply(scheme);
  media()?.addEventListener('change', () => {
    const current = useThemeStore.getState();
    if (current.choice !== 'system') return;
    const next = resolve('system');
    apply(next);
    set({ scheme: next });
  });
  return {
    choice,
    scheme,
    setChoice: (next) => {
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* A refused write keeps the choice for this tab only. */
      }
      const resolved = resolve(next);
      apply(resolved);
      set({ choice: next, scheme: resolved });
    },
  };
});

/** The scheme drawn now. Components that paint with a canvas redraw when it changes. */
export const useScheme = (): Scheme => useThemeStore((state) => state.scheme);
